import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, take, timeout } from 'rxjs/operators';
import { OrganizationProvider } from '../../../../../aumico-lib-users-fe/src/public-api';

/**
 * REV-21518 — ABCP-Navigation-Preview redirect (interim; see ADR 0002).
 *
 * Guards the `:id/cpmm` route: if the current organization has the `abcpPreview` feature flag set,
 * it funnels every `:id/cpmm/**` entry to the `:id/abcp/**` sibling — so opted-in orgs land on the
 * new vertical navigation WITHOUT changing the ~15 hard-coded `/cpmm` call-sites or the CpmmPage host.
 * `null`/absent flag = no preview = `/cpmm` stays (all other orgs unaffected).
 *
 * Interim by design: the flag is set via the seed (no admin UI, no migration yet). The "finished"
 * rollout (org-model migration + admin UI) is a later slice — ADR 0002.
 */
@Injectable({ providedIn: 'root' })
export class AbcpPreviewRedirectGuard implements CanActivate {
    constructor(
        private readonly organizationProvider: OrganizationProvider,
        private readonly router: Router,
    ) {}

    canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
        return this.organizationProvider.getCurrentOrganization$().pipe(
            // Wait for the current org to actually load (the selector emits null first).
            filter((organization) => organization != null),
            take(1),
            // Never hang a navigation on a slow/failed org fetch — fall back to /cpmm.
            timeout(10_000),
            map((organization) => {
                if (!organization.abcpPreview) {
                    return true;
                }
                // Opted-in → redirect to the abcp sibling, preserving the child path + query string.
                return this.router.parseUrl(state.url.replace('/cpmm', '/abcp'));
            }),
            catchError(() => of(true)),
        );
    }
}
